/**
 * RoadmapAgentMarkdown.tsx — small markdown subset for agent replies.
 * Ticket links become cards so FR-### is clickable even when the model
 * wraps the markdown in bold (that used to print as raw `[title](url)`).
 */

import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { FeatureRequestStatus } from '@/api/endpoints/roadmap';
import { RequestStatusBadge } from './RoadmapBadges';
import { TICKET_STATUS_LABELS } from '../lib/ticket-filters';

const TICKET_LINE =
  /^(?:\*\*)?\[([^\]]+)\]\((\/admin\/roadmap\/requests\/\d+)\)(?:\*\*)?\s*(.*)$/;
const INLINE_MARKUP = /\[([^\]]+)\]\(([^)]+)\)|\*\*([\s\S]+?)\*\*|`([^`]+)`/g;

function statusFromMeta(meta: string): FeatureRequestStatus | null {
  const entries = (Object.entries(TICKET_STATUS_LABELS) as Array<
    [FeatureRequestStatus, string]
  >).sort((a, b) => b[1].length - a[1].length);
  for (const [status, label] of entries) {
    if (new RegExp(`\\b${label.replaceAll(' ', '\\s+')}\\b`, 'i').test(meta)) {
      return status;
    }
  }
  for (const [status] of entries) {
    const slug = status.replaceAll('_', ' ');
    if (new RegExp(`\\b${slug}\\b`, 'i').test(meta)) return status;
  }
  return null;
}

function parseTicketLine(text: string): { label: string; href: string; meta: string } | null {
  const match = TICKET_LINE.exec(text.trim());
  if (!match?.[1] || !match[2]) return null;
  return { label: match[1], href: match[2], meta: (match[3] ?? '').trim() };
}

function AgentLink({ label, href }: { label: string; href: string }) {
  if (href.startsWith('/')) {
    return (
      <Link to={href} className="font-medium text-primary underline underline-offset-2">
        {label}
      </Link>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="font-medium text-primary underline underline-offset-2"
    >
      {label}
    </a>
  );
}

function TicketMatchCard({
  label,
  href,
  meta,
}: {
  label: string;
  href: string;
  meta?: string;
}) {
  const fr = /^FR-\d+\b/i.exec(label.trim());
  const title = fr ? label.slice(fr[0].length).trim() : label;
  const status = meta ? statusFromMeta(meta) : null;
  return (
    <Link
      to={href}
      className="group flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 no-underline shadow-sm transition-colors hover:border-primary/40 hover:bg-muted/30"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-[11px] font-semibold tracking-wide text-primary">
            {fr?.[0] ?? 'Ticket'}
          </p>
          {status ? <RequestStatusBadge status={status} /> : null}
        </div>
        <p className="truncate text-sm font-medium text-foreground">{title || label}</p>
        {meta ? <p className="truncate text-[11px] text-muted-foreground">{meta}</p> : null}
      </div>
      <ChevronRight
        className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary"
        aria-hidden
      />
    </Link>
  );
}

function InlineMarkdown({ text }: { text: string }) {
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;
  INLINE_MARKUP.lastIndex = 0;
  let match = INLINE_MARKUP.exec(text);
  while (match) {
    if (match.index > last) {
      nodes.push(<span key={`t-${key++}`}>{text.slice(last, match.index)}</span>);
    }
    if (match[1] != null && match[2] != null) {
      nodes.push(<AgentLink key={`l-${key++}`} label={match[1]} href={match[2]} />);
    } else if (match[3] != null) {
      nodes.push(
        <strong key={`b-${key++}`}>
          <InlineMarkdown text={match[3]} />
        </strong>,
      );
    } else if (match[4] != null) {
      nodes.push(
        <code key={`c-${key++}`} className="rounded bg-muted px-1 text-[0.85em]">
          {match[4]}
        </code>,
      );
    }
    last = match.index + match[0].length;
    match = INLINE_MARKUP.exec(text);
  }
  if (last < text.length) {
    nodes.push(<span key={`t-${key++}`}>{text.slice(last)}</span>);
  }
  return <>{nodes}</>;
}

/**
 * Line-oriented markdown-lite: bullets, numbered lists, ticket cards, inline.
 */
export function AgentMarkdown({ content }: { content: string }) {
  const lines = content.split('\n');
  return (
    <div className="space-y-2 text-[13px] leading-6 text-foreground">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={`gap-${i}`} className="h-1" />;

        const ticket = parseTicketLine(trimmed);
        if (ticket) {
          return (
            <TicketMatchCard
              key={`${i}-${line}`}
              label={ticket.label}
              href={ticket.href}
              meta={ticket.meta}
            />
          );
        }

        const bullet = /^\s*[-•]\s+(.*)$/.exec(line);
        if (bullet) {
          const body = (bullet[1] ?? '').trim();
          const bulletTicket = parseTicketLine(body);
          if (bulletTicket) {
            return (
              <TicketMatchCard
                key={`${i}-${line}`}
                label={bulletTicket.label}
                href={bulletTicket.href}
                meta={bulletTicket.meta}
              />
            );
          }
          return (
            <div key={`${i}-${line}`} className="flex gap-2 pl-0.5">
              <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-muted-foreground" aria-hidden />
              <span>
                <InlineMarkdown text={body} />
              </span>
            </div>
          );
        }

        const numbered = /^\s*\d+\.\s+(.*)$/.exec(line);
        if (numbered) {
          return (
            <p key={`${i}-${line}`}>
              <InlineMarkdown text={trimmed} />
            </p>
          );
        }

        return (
          <p key={`${i}-${line}`}>
            <InlineMarkdown text={line} />
          </p>
        );
      })}
    </div>
  );
}

